import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Collapse,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Divider,
  Grid,
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  Refresh,
  BugReport,
  CheckCircle,
  Error as ErrorIcon,
  Schedule,
  PlayArrow,
} from '@mui/icons-material';
import { adminService } from '../api/services';

const AutoGenDebugPanel = ({ configs }) => {
  const [expanded, setExpanded] = useState(false);
  const [celeryStatus, setCeleryStatus] = useState(null);
  const [configStatuses, setConfigStatuses] = useState({});
  const [loading, setLoading] = useState(false);

  const fetchCeleryStatus = async () => {
    try {
      const response = await adminService.getCeleryStatus();
      setCeleryStatus(response.data);
    } catch (error) {
      console.error('Error fetching Celery status:', error);
      setCeleryStatus({ status: 'error', error: error.message });
    }
  };

  const fetchConfigStatus = async (configId) => {
    try {
      const response = await adminService.getExecutionStatus(configId);
      setConfigStatuses(prev => ({
        ...prev,
        [configId]: response.data
      }));
    } catch (error) {
      console.error(`Error fetching status for config ${configId}:`, error);
    }
  };

  const fetchAllStatuses = async () => {
    setLoading(true);
    await fetchCeleryStatus();
    for (const config of configs) {
      await fetchConfigStatus(config.id);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (expanded && configs.length > 0) {
      fetchAllStatuses();
    }
  }, [expanded, configs]);

  const handleRefresh = () => {
    fetchAllStatuses();
  };

  return (
    <Card sx={{ mt: 2, bgcolor: 'background.default' }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <BugReport color="primary" />
            <Typography variant="h6">
              Consola de Debug
            </Typography>
            {celeryStatus && (
              <Chip
                label={celeryStatus.status === 'online' ? 'Online' : 'Offline'}
                color={celeryStatus.status === 'online' ? 'success' : 'error'}
                size="small"
              />
            )}
          </Box>
          <Box>
            <IconButton onClick={handleRefresh} disabled={loading} size="small">
              <Refresh />
            </IconButton>
            <IconButton onClick={() => setExpanded(!expanded)} size="small">
              {expanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
        </Box>

        <Collapse in={expanded}>
          <Divider sx={{ my: 2 }} />

          {loading && (
            <Box display="flex" justifyContent="center" my={2}>
              <CircularProgress size={24} />
            </Box>
          )}

          {/* Celery Workers Status */}
          {celeryStatus && (
            <Box mb={3}>
              <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                Estado de Celery Workers
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2, bgcolor: 'background.paper' }}>
                    <Typography variant="caption" color="text.secondary">
                      Workers Activos
                    </Typography>
                    <Typography variant="h4">
                      {celeryStatus.workers_count || 0}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2, bgcolor: 'background.paper' }}>
                    <Typography variant="caption" color="text.secondary">
                      Tareas Activas
                    </Typography>
                    <Typography variant="h4">
                      {celeryStatus.active_tasks_count || 0}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2, bgcolor: 'background.paper' }}>
                    <Typography variant="caption" color="text.secondary">
                      Estado
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1} mt={1}>
                      {celeryStatus.status === 'online' ? (
                        <CheckCircle color="success" />
                      ) : (
                        <ErrorIcon color="error" />
                      )}
                      <Typography variant="h6">
                        {celeryStatus.status}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>

              {celeryStatus.workers && celeryStatus.workers.length > 0 && (
                <Box mt={2}>
                  <Typography variant="caption" color="text.secondary">
                    Workers Detectados:
                  </Typography>
                  {celeryStatus.workers.map((worker, idx) => (
                    <Chip
                      key={idx}
                      label={`${worker.name} (${worker.pool})`}
                      size="small"
                      sx={{ mr: 1, mt: 1 }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          )}

          {/* Configurations Status */}
          {Object.keys(configStatuses).length > 0 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                Estado de Configuraciones
              </Typography>
              <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Configuración</TableCell>
                      <TableCell align="center">Posts Hoy</TableCell>
                      <TableCell align="center">Ventana</TableCell>
                      <TableCell align="center">Puede Ejecutar</TableCell>
                      <TableCell>Última Ejecución</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {configs.map(config => {
                      const status = configStatuses[config.id];
                      if (!status) return null;

                      return (
                        <TableRow key={config.id}>
                          <TableCell>
                            <Typography variant="body2" fontWeight="bold">
                              {status.config_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {status.scheduled_time}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={`${status.posts_today}/${status.max_posts_per_day}`}
                              size="small"
                              color={status.posts_today < status.max_posts_per_day ? 'success' : 'warning'}
                            />
                          </TableCell>
                          <TableCell align="center">
                            {status.in_time_window ? (
                              <Chip label="En Ventana" color="success" size="small" />
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                {status.window_start} - {status.window_end}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {status.can_execute ? (
                              <CheckCircle color="success" fontSize="small" />
                            ) : (
                              <ErrorIcon color="error" fontSize="small" />
                            )}
                          </TableCell>
                          <TableCell>
                            {status.last_execution ? (
                              <Typography variant="caption">
                                {new Date(status.last_execution).toLocaleString('es-VE')}
                              </Typography>
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                Nunca
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Current Time Display */}
          <Box mt={2}>
            <Alert severity="info" icon={<Schedule />}>
              <Typography variant="caption">
                Hora actual del servidor: {new Date().toLocaleTimeString('es-VE')}
              </Typography>
            </Alert>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default AutoGenDebugPanel;
